import { useDeleteChatFolder, useDeleteFolder } from "@/api/folders";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  FOLDER_RESOURCE_TYPE_FLOW_SESSION,
  type Folder,
} from "@cohesive/api-client";
import toast from "react-hot-toast";

export interface DeleteFolderDialogProps {
  onClose: () => void;
  workspaceId: string;
  folder: Folder;
  onSuccess?: () => void;
}

/**
 * Confirm archiving a shared canvas/chat folder. Today this removes the folder
 * and re-parents its subfolders and resources up one level (the backend
 * guarantees this), so nothing inside is touched — the copy says exactly that.
 * Cascading the archive to the folder's contents is a planned follow-up.
 */
export function DeleteFolderDialog({
  onClose,
  workspaceId,
  folder,
  onSuccess,
}: DeleteFolderDialogProps) {
  const isChatFolder =
    folder.resource_type === FOLDER_RESOURCE_TYPE_FLOW_SESSION;
  const deleteCanvasFolder = useDeleteFolder(workspaceId);
  const deleteChatFolder = useDeleteChatFolder(workspaceId);
  const deleteFolder = isChatFolder ? deleteChatFolder : deleteCanvasFolder;
  const contentsLabel = isChatFolder ? "chats" : "canvases";

  const handleConfirm = () => {
    deleteFolder.mutate(
      { folderId: folder.id },
      {
        onSuccess: () => {
          toast.success("Folder archived");
          onSuccess?.();
          onClose();
        },
        onError: () => toast.error("Failed to archive folder"),
      },
    );
  };

  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent
        className="sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{`Archive "${folder.name}"?`}</AlertDialogTitle>
          <AlertDialogDescription>
            {`Its folders and ${contentsLabel} move up one level — nothing inside is archived.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteFolder.isPending}>
            Cancel
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleteFolder.isPending}
            isPending={deleteFolder.isPending}
          >
            Archive Folder
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
